import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { BOE_PROGRAM_DOC_IDS } from '../../../shared/constants/programBudget';
import { BudgetBreakdown } from '../../../shared/ui/chart/BudgetBreakdown';
import { buildBoeOverview, boeOverviewOptions, boeCostBreakdown } from '../data/boeOverview';
const compact = value => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',notation:'compact',maximumFractionDigits:1}).format(value);
const fy = year => `${year}–${year+1}`;

export default function BoeBudgetOverview() {
  const [documents,setDocuments] = useState(null);
  const [selection,setSelection] = useState('');
  const [error,setError] = useState(false);
  const [retry,setRetry] = useState(0);
  useEffect(() => {
    let active=true;
    setError(false);
    Promise.all(BOE_PROGRAM_DOC_IDS.map(async id => {
      const snapshot=await getDoc(doc(db,'publicSchools',id));
      return [id,snapshot.exists()?snapshot.data():{}];
    })).then(entries => {
      if(!active) return;
      const next=Object.fromEntries(entries);
      const latest=boeOverviewOptions(next)[0];
      setDocuments(next); setSelection(latest?`${latest.year}:${latest.type}`:'');
    }).catch(() => {if(active) setError(true);});
    return () => {active=false;};
  },[retry]);
  const options=documents?boeOverviewOptions(documents):[];
  const [yearKey,type]=selection.split(':');
  const year=Number(yearKey);
  const overview=selection?buildBoeOverview(documents,year,type):null;
  const prior=overview?buildBoeOverview(documents,year-1,'Approved'):null;
  const change=prior?.complete && prior.total>0?(overview.total/prior.total-1)*100:null;
  const ranked=overview?[...overview.programs].sort((a,b)=>b.value-a.value):[];
  const programRows=overview?[...ranked.slice(0,5),{id:'remaining',label:'All other programs',value:ranked.slice(5).reduce((sum,row)=>sum+row.value,0)}]:[];
  const costs=overview?boeCostBreakdown(documents,year,type):[];
  const costTotal=costs.reduce((sum,row)=>sum+row.value,0);
  const costsReconcile=overview && Math.abs(costTotal-overview.total)<1;
  return <section aria-label="Board of Education budget overview" className="space-y-5">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div><h3 className="text-2xl font-bold text-slate-950">Our schools, at a glance</h3><p className="mt-2 text-sm leading-6 text-slate-600">How the district invests in learning, people, and school services.</p></div>
      {!!options.length && <label className="flex items-center gap-3 text-sm font-medium text-slate-700">Fiscal year<select className="select select-bordered bg-white" value={selection} onChange={event=>setSelection(event.target.value)}>{options.map(option=><option key={option.year} value={`${option.year}:${option.type}`}>{fy(option.year)} · {option.type}</option>)}</select></label>}
    </div>
    {error?<p role="alert" className="rounded-lg bg-orange-50 p-4 text-orange-900">Unable to load the school budget. <button className="underline" onClick={()=>setRetry(value=>value+1)}>Try again</button></p>:!documents?<p role="status">Loading school budget overview…</p>:!overview?<p>A complete school budget overview is not available yet. Explore the individual reports below.</p>:<>
      <div className="grid gap-4 md:grid-cols-3">
        {[
          [`${type} school budget`,compact(overview.total),`FY ${fy(year)} · all ${overview.programs.length} programs`,'bg-[#243f86] text-white'],
          ['Change from prior budget',change===null?'—':`${change>=0?'+':''}${change.toFixed(1)}%`,prior?.complete?`Compared with FY ${fy(year-1)} approved`:'Prior approved budget unavailable','bg-violet-100 text-violet-950'],
          ['Largest program',compact(ranked[0].value),ranked[0].label,'bg-orange-100 text-orange-950'],
        ].map(([label,value,note,style])=><div key={label} className={`rounded-lg p-6 ${style}`}><p className="text-sm font-medium">{label}</p><p className="mt-3 text-4xl font-bold tracking-tight tabular-nums">{value}</p><p className="mt-3 text-sm">{note}</p></div>)}
      </div>
      {type==='Proposed' && <p className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-950">This is a proposed budget, subject to approval. Choose an earlier year to explore approved budgets.</p>}
      <div className={`grid gap-5 ${costsReconcile?'lg:grid-cols-2':''}`}>
        <BudgetBreakdown title="Where the school budget goes" rows={programRows} total={overview.total}/>
        {costsReconcile && <BudgetBreakdown title="What the budget supports" rows={costs} total={overview.total}/>}
      </div>
      <p className="text-xs leading-5 text-slate-500">Source: Glastonbury Public Schools program budget data. Fiscal years run July–June. Figures are {type.toLowerCase()} budgets, not actual spending. The overview shows the latest year with all programs available and has its own year selection.</p>
    </>}
  </section>;
}
